import { ReactNode } from "react";

interface ProductMockupProps {
  label: string;
  children: ReactNode;
  className?: string;
  /**
   * Reserve a fixed minimum height for the content area to prevent layout shift.
   * Tailwind classes, e.g. "min-h-[460px] md:min-h-[520px]".
   * Prefer `contentHeight` for true CLS prevention.
   */
  contentMinHeight?: string;
  /**
   * Reserve an EXACT (fixed) height for the content area. Combined with
   * overflow-hidden, this guarantees the outer page never reflows as
   * internal animated content appears/swaps over time.
   * Tailwind classes, e.g. "h-[520px] md:h-[400px]".
   */
  contentHeight?: string;
}

/**
 * Shared "app window" chrome for product mockups.
 * Hairline border, subtle header bar with traffic-light dots and a label.
 * Owns the visual consistency across Build / Deploy / Govern mocks.
 */
const ProductMockup = ({
  label,
  children,
  className = "",
  contentMinHeight = "",
  contentHeight = "",
}: ProductMockupProps) => {
  // When a fixed height is requested, clip overflow so internal animations
  // never push the surrounding page down.
  const sizeClass = contentHeight ? `${contentHeight} md:overflow-hidden` : contentMinHeight;
  return (
    <div
      className={`relative rounded-lg border border-border bg-card shadow-card overflow-hidden ${className}`}
    >
      {/* Header bar */}
      <div className="flex items-center gap-3 border-b border-border bg-background/40 px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-foreground/15" aria-hidden />
          <span className="h-2 w-2 rounded-full bg-foreground/15" aria-hidden />
          <span className="h-2 w-2 rounded-full bg-foreground/15" aria-hidden />
        </div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
          {label}
        </p>
      </div>

      {/* Content area — contentHeight reserves an EXACT box to prevent CLS */}
      <div className={`relative ${sizeClass}`}>{children}</div>
    </div>
  );
};

export default ProductMockup;
